import React, {
  Suspense,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { Canvas, useThree } from '@react-three/fiber'
import { OrbitControls, useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { getProduct } from '~/components/ThreeDModel/products'
import { GroundShadow } from '~/components/ThreeDModel/GroundShadow'
import { applyMaterialToObject } from '~/components/ThreeDModel/materials/applyMaterial'
import { captureScreenshot } from '~/utils/screenshotUtils'
import { exportSceneAsGLB, triggerDownload } from '~/utils/exportModelUtils'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import styles from './TableViewer.module.css'
import { ViewerZoomControls } from '~/components/ViewerZoomControls/ViewerZoomControls'
import { WebGLErrorBoundary } from '~/components/WebGLErrorBoundary/WebGLErrorBoundary'
import { ModelLoadingOverlay } from '~/components/ThreeDModel/ModelLoadingOverlay'
import { useSceneDebug } from '~/components/ThreeDModel/SceneDebugInspector'

const MODEL_URL = '/cdn/assets/3d-models/table-compressed.glb'
const MODEL_NATIVE_WIDTH_CM = 300
const MODEL_NATIVE_DEPTH_CM = 90

useGLTF.preload(MODEL_URL)

export interface TableViewerRef {
  captureScreenshot: (
    format?: 'image/png' | 'image/jpeg',
    quality?: number
  ) => string | null
  exportModel: () => Promise<void>
}

interface TableViewerProps {
  width: number
  depth: number
  selectedTexture?: string
  onShare?: () => void
  onExport?: () => Promise<void> | void
}

const TableModel: React.FC<{
  scale: [number, number, number]
  selectedTexture?: string
}> = ({ scale, selectedTexture }) => {
  const { scene } = useGLTF(MODEL_URL)
  const groupRef = useRef<THREE.Group>(null)

  const clonedScene = useMemo(() => scene.clone(), [scene])

  useEffect(() => {
    if (groupRef.current && selectedTexture) {
      applyMaterialToObject(groupRef.current, selectedTexture)
    }
  }, [selectedTexture, clonedScene])

  return (
    <group ref={groupRef} scale={scale}>
      <primitive object={clonedScene} />
    </group>
  )
}

const TableLights = getProduct('table').Lights

const TableScene: React.FC<TableViewerProps> = ({
  width,
  depth,
  selectedTexture,
}) => {
  const modelScale = useMemo<[number, number, number]>(
    () => [width / MODEL_NATIVE_WIDTH_CM, 1, depth / MODEL_NATIVE_DEPTH_CM],
    [width, depth]
  )

  return (
    <>
      <fog attach="fog" args={['#eae9e8', 5000, 12000]} />

      <TableLights enableShadows shadowQuality="high" />

      <GroundShadow
        width={width + 5500}
        depth={depth + 5500}
        opacity={0.25}
        blur={1.5}
      />

      <Suspense fallback={null}>
        <TableModel scale={modelScale} selectedTexture={selectedTexture} />
      </Suspense>
    </>
  )
}

const SceneCapture: React.FC<{
  rendererRef: React.MutableRefObject<THREE.WebGLRenderer | null>
  sceneRef: React.MutableRefObject<THREE.Scene | null>
  cameraRef: React.MutableRefObject<THREE.Camera | null>
}> = ({ rendererRef, sceneRef, cameraRef }) => {
  const { gl, scene, camera } = useThree()
  useEffect(() => {
    rendererRef.current = gl
    sceneRef.current = scene
    cameraRef.current = camera
  }, [gl, scene, camera, rendererRef, sceneRef, cameraRef])
  return null
}

export const TableViewer = forwardRef<TableViewerRef, TableViewerProps>(function TableViewer(
  { width, depth, selectedTexture, onShare, onExport }, ref) {
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
    const sceneRef = useRef<THREE.Scene | null>(null)
    const cameraRef = useRef<THREE.Camera | null>(null)

    const orbitRef = useRef<OrbitControlsImpl>(null)

    const debug = useSceneDebug()

    const handleZoomIn = useCallback(() => {
      const controls = orbitRef.current
      if (!controls) return
      const dir = controls.object.position.clone().sub(controls.target).normalize()
      const dist = controls.object.position.distanceTo(controls.target)
      const newDist = Math.max(controls.minDistance, dist * 0.75)
      controls.object.position.copy(controls.target.clone().addScaledVector(dir, newDist))
      controls.update()
    }, [])

    const handleZoomOut = useCallback(() => {
      const controls = orbitRef.current
      if (!controls) return
      const dir = controls.object.position.clone().sub(controls.target).normalize()
      const dist = controls.object.position.distanceTo(controls.target)
      const newDist = Math.min(controls.maxDistance, dist * 1.33)
      controls.object.position.copy(controls.target.clone().addScaledVector(dir, newDist))
      controls.update()
    }, [])

    useImperativeHandle(ref, () => ({
      captureScreenshot: (
        format: 'image/png' | 'image/jpeg' = 'image/jpeg',
        quality = 0.85
      ) => {
        if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
          return null
        }
        const scene = sceneRef.current
        const prevBg = scene.background
        scene.background = new THREE.Color('#eae9e8')

        const result = captureScreenshot(
          rendererRef.current,
          scene,
          cameraRef.current,
          format,
          quality
        )

        scene.background = prevBg
        return result
      },
      exportModel: async () => {
        const scene = sceneRef.current
        if (!scene) return

        const glbData = await exportSceneAsGLB(scene)
        triggerDownload(glbData, 'table-config.glb')
      },
    }))

    return (
      <WebGLErrorBoundary>
        <div className={styles.container}>
          <Canvas
            camera={{
              position: [-2000, 1500, 3000],
              fov: 45,
              near: 1,
              far: 15000,
            }}
            shadows
            dpr={[1, 1.75]}
            gl={{
              powerPreference: 'high-performance',
              preserveDrawingBuffer: true,
              alpha: true,
            }}
            className={styles.canvas}
            style={{ background: 'transparent' }}
          >
            <SceneCapture
              rendererRef={rendererRef}
              sceneRef={sceneRef}
              cameraRef={cameraRef}
            />

            <OrbitControls
              ref={orbitRef}
              makeDefault
              enablePan={true}
              enableZoom={false}
              enableRotate={true}
              enableDamping={false}
              minAzimuthAngle={-Math.PI / 2 + 0.5}
              maxAzimuthAngle={Math.PI / 2 - 0.5}
              minPolarAngle={0.3}
              maxPolarAngle={Math.PI / 2 + 0.2}
              target={[0, 400, 0]}
            />

            <TableScene
              width={width}
              depth={depth}
              selectedTexture={selectedTexture}
            />

            {debug.canvasNode}
          </Canvas>
          {debug.overlayNode}
          <ModelLoadingOverlay />
          <ViewerZoomControls onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onShare={onShare} onExport={onExport} />
        </div>
      </WebGLErrorBoundary>
    )
  }
)
